import { useState, useRef, useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Delete, CheckCircle2, Loader2, ScanLine } from "lucide-react";

export default function Checkin() {
  const { currentStore } = useAuth();
  const [kin, setKin] = useState("");
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const timerRef = useRef(null);

  const clearTimer = () => { if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; } };
  useEffect(() => clearTimer, []);

  const press = (d) => { clearTimer(); setStatus(null); setKin((k) => (k + d).slice(0, 20)); };
  const back = () => { clearTimer(); setStatus(null); setKin((k) => k.slice(0, -1)); };

  const submit = async () => {
    if (!currentStore) { toast.error("Select a store first"); return; }
    const clean = kin.trim().toUpperCase().replace(/\s/g, "");
    if (!clean) { setStatus({ type: "error", message: "Enter the customer's KIN number" }); return; }
    clearTimer();
    setSubmitting(true);
    try {
      const { data } = await api.post("/transactions/checkin", { store_id: currentStore.id, kin_number: clean });
      setStatus({ type: "success", message: data.message, ref: data.transaction_number });
      setKin("");
      timerRef.current = setTimeout(() => setStatus(null), 4000);
    } catch (e) {
      setStatus({ type: "error", message: apiError(e) });
    } finally {
      setSubmitting(false);
    }
  };

  const KEY = "h-20 bg-secondary hover:bg-secondary/70 active:bg-secondary/50 rounded-sm transition-colors duration-150";

  return (
    <DashboardLayout title="Check-In Desk">
      <div className="max-w-xl mx-auto w-full" data-testid="checkin-desk">
        <p className="text-sm text-muted-foreground text-center mb-6">
          Enter the customer's KIN number to add them to the queue{currentStore ? ` at ${currentStore.store_name}` : ""}.
        </p>

        {status?.type === "success" ? (
          <div className="bg-alert-green/10 border-2 border-alert-green p-12 text-center rounded-sm" data-testid="checkin-success">
            <CheckCircle2 size={64} className="text-alert-green mx-auto" />
            <div className="font-display text-3xl font-bold mt-4">{status.message}</div>
            {status.ref && <div className="font-mono-t text-sm text-muted-foreground mt-2">{status.ref}</div>}
            <Button variant="outline" className="mt-6" onClick={() => { clearTimer(); setStatus(null); }} data-testid="checkin-next-btn">Next customer</Button>
          </div>
        ) : (
          <>
            <div className="h-24 border-b-4 border-white/80 flex items-center justify-center mb-4">
              <span className="font-mono-t text-6xl font-bold tracking-widest" data-testid="checkin-kin-display">
                {kin || <span className="text-white/25">KIN</span>}
              </span>
            </div>
            {status?.type === "error" && <p className="text-alert-red text-center mb-4 font-medium" data-testid="checkin-error">{status.message}</p>}

            <div className="grid grid-cols-3 gap-3">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
                <button key={d} onClick={() => press(d)} data-testid={`checkin-key-${d}`} className={`${KEY} font-mono-t text-4xl font-bold`}>{d}</button>
              ))}
              <button onClick={() => press("KIN")} className={`${KEY} font-display text-xl font-bold`} data-testid="checkin-key-prefix">KIN</button>
              <button onClick={() => press("0")} className={`${KEY} font-mono-t text-4xl font-bold`} data-testid="checkin-key-0">0</button>
              <button onClick={back} className={`${KEY} flex items-center justify-center`} data-testid="checkin-key-back"><Delete size={28} /></button>
            </div>

            <Button onClick={submit} disabled={submitting} data-testid="checkin-submit"
              className="mt-6 w-full h-20 text-3xl font-display font-bold rounded-full gap-3">
              {submitting ? <Loader2 className="animate-spin" size={28} /> : (<><ScanLine size={26} /> CHECK IN</>)}
            </Button>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
